import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { ConexionBanner } from '../../components/ConexionBanner';
import { buildApiUrl } from '../../utils/apiUrl';
import type { AppBoleta, AppBoletaCampo, AppBoom } from '../../types/empacadora';
import { useGpsTracker } from './useGpsTracker';

function injectBoomeroResponsiveCSS() {
  const id = 'boomero-responsive-css';
  if (document.getElementById(id)) return;

  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    .boomero-page,
    .boomero-modal-card {
      box-sizing: border-box;
      max-width: 100%;
    }

    .boomero-field-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }

    .boomero-calibration-row {
      display: flex;
      gap: 12px;
      margin-bottom: 12px;
    }

    .boomero-chip-row {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    @media (max-width: 640px) {
      .boomero-page {
        padding: 12px !important;
      }

      .boomero-list-card {
        padding: 14px !important;
        border-radius: 12px !important;
      }

      .boomero-list-title {
        font-size: 18px !important;
      }

      .boomero-modal-shell {
        padding: 10px !important;
      }

      .boomero-modal-card {
        padding: 16px !important;
        border-radius: 14px !important;
        min-height: calc(100dvh - 20px);
      }

      .boomero-modal-header {
        align-items: stretch !important;
        flex-direction: column !important;
        gap: 10px !important;
      }

      .boomero-modal-header button {
        width: 100%;
        min-height: 40px;
      }

      .boomero-field-grid {
        grid-template-columns: 1fr;
      }

      .boomero-calibration-row {
        flex-direction: column;
      }

      .boomero-chip-row > button {
        flex: 1 1 calc(50% - 4px);
        min-height: 44px;
      }

      .boomero-save-button {
        font-size: 15px !important;
        padding: 14px 12px !important;
      }
    }
  `;
  document.head.appendChild(style);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtN(n?: number | null, dec = 2) {
  if (n == null) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

interface GpsPos {
  lat: number;
  lng: number;
  accuracy: number;
  at: string;
}

function obtenerUbicacion(timeoutMs = 12000, maximumAgeMs = 30000): Promise<GpsPos> {
  // Acepta lecturas recientes (hasta 30s por defecto) para que el receptor GPS no
  // tenga que arrancar desde cero — evita deadlocks cuando un watchPosition está activo.
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Este dispositivo no soporta GPS.'));
      return;
    }
    // Salvavidas: si el navegador no responde, fallar duro al doble del timeout.
    const failsafe = setTimeout(() => {
      reject(new Error('GPS no respondió. Revisá permisos o intentá de nuevo.'));
    }, timeoutMs * 2);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(failsafe);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          at: new Date(pos.timestamp).toISOString(),
        });
      },
      (err) => {
        clearTimeout(failsafe);
        if (err.code === err.PERMISSION_DENIED) reject(new Error('Permiso de ubicación denegado. Activá la ubicación del dispositivo.'));
        else if (err.code === err.POSITION_UNAVAILABLE) reject(new Error('No se pudo obtener la ubicación (sin señal GPS).'));
        else if (err.code === err.TIMEOUT) reject(new Error('Tiempo agotado al obtener la ubicación. Intentá de nuevo.'));
        else reject(new Error(err.message || 'Error al obtener ubicación.'));
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: maximumAgeMs },
    );
  });
}

function fmtCoord(lat?: number | null, lng?: number | null) {
  if (lat == null || lng == null) return '—';
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
}

interface ClimaSnapshot {
  temp_c: number;
  humedad_pct: number;
  viento_kmh: number;
  viento_dir_deg: number;
  precipitacion_mm: number;
  codigo: number;
}

// Consulta Open-Meteo (API pública, sin API key) y devuelve snapshot actual.
// La API responde rápido (<500ms) y no requiere auth. timezone=auto evita desfases.
async function consultarClimaOpenMeteo(lat: number, lng: number, timeoutMs = 8000): Promise<ClimaSnapshot> {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,relative_humidity_2m,precipitation,weather_code,wind_speed_10m,wind_direction_10m&timezone=auto`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const resp = await fetch(url, { signal: ctrl.signal });
    if (!resp.ok) throw new Error(`Open-Meteo HTTP ${resp.status}`);
    const json: any = await resp.json();
    const c = json?.current ?? {};
    return {
      temp_c: Number(c.temperature_2m ?? 0),
      humedad_pct: Number(c.relative_humidity_2m ?? 0),
      viento_kmh: Number(c.wind_speed_10m ?? 0),
      viento_dir_deg: Number(c.wind_direction_10m ?? 0),
      precipitacion_mm: Number(c.precipitation ?? 0),
      codigo: Number(c.weather_code ?? 0),
    };
  } finally {
    clearTimeout(timer);
  }
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const MARCHAS = ['1ra', '2da', '3ra', '4ta', 'N/A'];

const CAMPO_VACIO = (empresaId: number, boletaId: number, boomeroNombre: string): AppBoletaCampo => ({
  empresa_id: empresaId,
  boleta_id: boletaId,
  boomero_nombre: boomeroNombre,
  boom_descripcion: '',
  rpm: undefined,
  presion_bar: undefined,
  marcha: '1ra',
  caldo_sobrante: false,
  caldo_sobrante_litros: undefined,
  clima_nublado: false,
  clima_lluvioso: false,
  clima_soleado: false,
  clima_ventoso: false,
  clima_no_apto: false,
  metodo_aspersion: false,
  metodo_inmersion: false,
  metodo_nebulizacion: false,
  cultivo: false,
  semillero: false,
  area_barbecho: false,
  observaciones: '',
  registrado_por: boomeroNombre,
});

// ── Sección con título ────────────────────────────────────────────────────────

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        fontSize: '11px', fontWeight: 700, color: 'var(--ink-muted)',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px',
      }}>{titulo}</div>
      {children}
    </div>
  );
}

// ── CheckChip ────────────────────────────────────────────────────────────────

function CheckChip({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!checked)} style={{
      padding: '10px 16px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
      cursor: 'pointer', border: `1.5px solid ${checked ? '#22c55e' : 'rgba(255,255,255,0.12)'}`,
      background: checked ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
      color: checked ? '#4ade80' : 'var(--ink-muted)', transition: 'all 0.15s',
    }}>
      {checked ? '✓ ' : ''}{label}
    </button>
  );
}

// ── NumInput ─────────────────────────────────────────────────────────────────

function NumInput({ label, value, onChange, placeholder = '0.00' }: {
  label: string; value?: number; onChange: (v?: number) => void; placeholder?: string;
}) {
  const [raw, setRaw] = useState(value != null ? String(value) : '');
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: '4px' }}>{label}</div>
      <input
        type="text" inputMode="decimal"
        value={raw}
        placeholder={placeholder}
        onFocus={e => e.target.select()}
        onChange={e => {
          const v = e.target.value.replace(/[^0-9.,]/g, '');
          setRaw(v);
          const n = parseFloat(v.replace(',', '.'));
          onChange(isNaN(n) ? undefined : n);
        }}
        onBlur={e => {
          const n = parseFloat(e.target.value.replace(',', '.'));
          if (!isNaN(n)) {
            setRaw(n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
            onChange(n);
          }
        }}
        style={{
          width: '100%', padding: '12px 14px', borderRadius: '10px', fontSize: '20px',
          fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
          border: '1.5px solid var(--line)', background: 'var(--surface-base)',
          color: '#f8fafc', outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export function BoomeroPage() {
  const empresaId = useEmpresaId();
  const [usuario, setUsuario]       = useState('');
  const [boletas, setBoletas]       = useState<AppBoleta[]>([]);
  const [loading, setLoading]       = useState(false);
  const [guardando, setGuardando]   = useState(false);
  const [msg, setMsg]               = useState<{ tipo: 'ok' | 'err'; texto: string } | null>(null);
  const [modal, setModal]           = useState<{ boleta: AppBoleta; campo: AppBoletaCampo } | null>(null);
  // Auto-save: indica estado de la última sincronización con BD
  const [autoSave, setAutoSave] = useState<'idle' | 'pendiente' | 'guardando' | 'guardado' | 'error'>('idle');
  const autoSaveTimerRef = useRef<number | null>(null);
  const ultimaCampoSerialRef = useRef<string>('');
  const [booms, setBooms]           = useState<AppBoom[]>([]);
  // Tracking GPS continuo: contador de puntos aceptados y guardados en BD
  const [trackPuntos, setTrackPuntos] = useState(0);
  const [trackGuardados, setTrackGuardados] = useState(0);

  // Activar tracker mientras haya inicio_at y todavía no haya fin_at
  // Se pausa temporalmente al capturar el GPS de fin (capturandoGps === 'fin')
  // para liberar el receptor y evitar bloqueos.
  const [capturandoGps, setCapturandoGps] = useState<null | 'inicio' | 'fin'>(null);
  const trackerActivo = !!modal && !!modal.campo.inicio_at && !modal.campo.fin_at && capturandoGps !== 'fin';
  // Boom activo para etiquetar el GPS continuo. Se calcula después de cargar
  // boomsAplicados (state declarado más abajo). Aquí guardamos sólo la ref.
  const boomActivoRef = useRef<number | null>(null);

  useGpsTracker({
    enabled: trackerActivo,
    empresaId,
    boletaId: modal?.campo.boleta_id ?? null,
    boomNum: boomActivoRef.current,
    onPuntoAceptado: () => setTrackPuntos((n) => n + 1),
    onFlushOk: (cant) => setTrackGuardados((n) => n + cant),
    onError: (m) => showMsg('err', m),
  });

  // Reset contadores al cerrar modal o cambiar boleta
  useEffect(() => {
    if (!modal) {
      setTrackPuntos(0);
      setTrackGuardados(0);
    }
  }, [modal?.campo.boleta_id, modal]);

  function showMsg(tipo: 'ok' | 'err', texto: string) {
    setMsg({ tipo, texto });
    setTimeout(() => setMsg(null), 3500);
  }

  // Inyectar keyframes para indicador de rastreo
  useEffect(() => {
    injectBoomeroResponsiveCSS();
    const id = 'boomero-pulse-gps';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `@keyframes pulse-gps { 0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.6); } 50% { box-shadow: 0 0 0 6px rgba(34,197,94,0); } }`;
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUsuario((data.user?.user_metadata?.nombre as string) || data.user?.email || 'boomero');
    });
    // Cargar booms activos de la empresa
    supabase
      .from('app_booms')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('activo', true)
      .order('nombre')
      .then(({ data }) => setBooms((data ?? []) as AppBoom[]));
  }, [empresaId]);

  interface ReporteResumen {
    boomero_nombre?: string | null;
    registrado_por?: string | null;
    registrado_at?: string | null;
    updated_at?: string | null;
    observaciones?: string | null;
    inicio_at?: string | null;
    fin_at?: string | null;
  }
  const [reportePorBoleta, setReportePorBoleta] = useState<Map<number, ReporteResumen>>(new Map());
  // Map boleta_id → Set de boom_num aplicados (finalizados por boomero; 0 = fracción)
  const [boomsPorBoleta, setBoomsPorBoleta] = useState<Map<number, Set<number>>>(new Map());
  // Map boleta_id → Set de boom_num cerrados por bodega (despachado) — listos para aplicar
  const [boomsDespPorBoleta, setBoomsDespPorBoleta] = useState<Map<number, Set<number>>>(new Map());

  const cargar = useCallback(async () => {
    setLoading(true);
    // El boomero debe ver boletas en 'pendiente' o 'en_proceso' que YA tengan
    // al menos un boom cerrado por bodega (estado='despachado'). Antes filtraba
    // solo 'en_proceso', pero con el flujo boom-por-boom la boleta sigue en
    // 'pendiente' hasta que bodega cierra TODOS los booms — y el boomero debe
    // poder arrancar apenas bodega cierre el primero.
    const [{ data }, { data: reportes }, { data: boomsAplic }, { data: boomsDesp }] = await Promise.all([
      supabase
        .from('app_boletas')
        .select('*')
        .eq('empresa_id', empresaId)
        .in('estado', ['pendiente', 'en_proceso'])
        .order('semana', { ascending: false })
        .order('lote_num'),
      supabase
        .from('app_boleta_campo')
        .select('boleta_id, boomero_nombre, registrado_por, registrado_at, updated_at, observaciones, inicio_at, fin_at')
        .eq('empresa_id', empresaId),
      supabase
        .from('app_boleta_booms_aplicados')
        .select('boleta_id, boom_num')
        .eq('empresa_id', empresaId),
      supabase
        .from('app_boleta_booms_despachados')
        .select('boleta_id, boom_num, estado')
        .eq('empresa_id', empresaId)
        .eq('estado', 'despachado'),
    ]);
    setLoading(false);
    // Solo mostrar boletas con al menos un boom despachado por bodega.
    const boletasConBoomDespachado = new Set(
      (boomsDesp ?? []).map((d: any) => Number(d.boleta_id))
    );
    const boletasVisibles = (data ?? []).filter((b: any) =>
      boletasConBoomDespachado.has(Number(b.id))
    );
    setBoletas(boletasVisibles as AppBoleta[]);
    // Mapa boleta_id → Set de boom_num cerrados por bodega
    const despMap = new Map<number, Set<number>>();
    (boomsDesp ?? []).forEach((d: any) => {
      const bid = Number(d.boleta_id);
      if (!despMap.has(bid)) despMap.set(bid, new Set());
      despMap.get(bid)!.add(Number(d.boom_num));
    });
    setBoomsDespPorBoleta(despMap);
    const map = new Map<number, ReporteResumen>();
    (reportes ?? []).forEach((r: any) => {
      map.set(Number(r.boleta_id), {
        boomero_nombre: r.boomero_nombre,
        registrado_por: r.registrado_por,
        registrado_at: r.registrado_at,
        updated_at: r.updated_at,
        observaciones: r.observaciones,
        inicio_at: r.inicio_at,
        fin_at: r.fin_at,
      });
    });
    setReportePorBoleta(map);
    const boomsMap = new Map<number, Set<number>>();
    (boomsAplic ?? []).forEach((b: any) => {
      const bid = Number(b.boleta_id);
      if (!boomsMap.has(bid)) boomsMap.set(bid, new Set());
      boomsMap.get(bid)!.add(Number(b.boom_num));
    });
    setBoomsPorBoleta(boomsMap);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Abrir formulario de campo ───────────────────────────────────────────────

  async function abrirFormulario(b: AppBoleta) {
    // buscar si ya hay un reporte previo
    const { data } = await supabase
      .from('app_boleta_campo')
      .select('*')
      .eq('boleta_id', b.id)
      .eq('empresa_id', empresaId)
      .maybeSingle();
    const campo = data
      ? (data as AppBoletaCampo)
      : CAMPO_VACIO(empresaId, b.id, usuario);
    setModal({ boleta: b, campo });
    cargarBoomsAplicados(b.id);
  }

  // ── Booms aplicados (uno por cada boom completo + fracción) ───────────────
  // Cada boom tiene su propio par inicio/fin con GPS y aplicado_por.
  // boom_num: 1..N para completos, 0 para fracción
  interface BoomAplicado {
    id?: number;
    boom_num: number;
    tipo: 'completo' | 'fraccion';
    litros?: number | null;
    inicio_at?: string | null;
    fin_at?: string | null;
    inicio_lat?: number | null;
    inicio_lng?: number | null;
    fin_lat?: number | null;
    fin_lng?: number | null;
    aplicado_por?: string | null;
  }
  const [boomsAplicados, setBoomsAplicados] = useState<BoomAplicado[]>([]);
  // Progreso del lado bodega: qué booms fueron preparados/cerrados. El boomero
  // solo puede iniciar un boom que bodega ya cerró ('despachado').
  interface BoomDespachado {
    boom_num: number;
    tipo: 'completo' | 'fraccion';
    estado: 'despachado' | 'descartado';
    despachado_por: string | null;
    despachado_at: string | null;
  }
  const [boomsDespachados, setBoomsDespachados] = useState<BoomDespachado[]>([]);
  // boom_num en proceso de capturar GPS (para deshabilitar botones momentáneamente)
  const [procesandoBoom, setProcesandoBoom] = useState<number | null>(null);

  // Mantener boomActivoRef sincronizado: el primero con inicio_at y sin fin_at
  useEffect(() => {
    const enCurso = boomsAplicados.find(b => b.inicio_at && !b.fin_at);
    boomActivoRef.current = enCurso ? enCurso.boom_num : null;
  }, [boomsAplicados]);

  async function cargarBoomsAplicados(boletaId: number) {
    // Cargar booms aplicados (lado boomero) y despachados (lado bodega) en paralelo.
    const [{ data: aplics }, { data: despachs }] = await Promise.all([
      supabase
        .from('app_boleta_booms_aplicados')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('boleta_id', boletaId)
        .order('boom_num'),
      supabase
        .from('app_boleta_booms_despachados')
        .select('boom_num, tipo, estado, despachado_por, despachado_at')
        .eq('empresa_id', empresaId)
        .eq('boleta_id', boletaId)
        .order('boom_num'),
    ]);
    setBoomsAplicados((aplics ?? []) as BoomAplicado[]);
    setBoomsDespachados((despachs ?? []) as BoomDespachado[]);
  }

  // Captura GPS en el momento (sin tracker continuo).
  async function capturarGpsPuntual(): Promise<{ lat: number | null; lng: number | null }> {
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('GPS no disponible'));
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
      });
      return { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch {
      return { lat: null, lng: null };
    }
  }

  // Iniciar la aplicación de un boom vía endpoint REST. El backend valida que
  // bodega haya cerrado este boom primero (estado='despachado'); si no, 409.
  async function iniciarBoom(boom_num: number, tipo: 'completo' | 'fraccion', litros: number) {
    if (!modal) return;
    setProcesandoBoom(boom_num);
    try {
      const { lat, lng } = await capturarGpsPuntual();
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${modal.boleta.id}/iniciar-boom-aplicacion`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ boom_num, tipo, litros, lat, lng }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        showMsg('err', j?.error || 'No se pudo iniciar el boom.');
        return;
      }
      await cargarBoomsAplicados(modal.boleta.id);
      showMsg('ok', `Boom ${tipo === 'fraccion' ? 'fracción' : boom_num} iniciado`);
    } finally {
      setProcesandoBoom(null);
    }
  }

  // Finalizar la aplicación de un boom vía endpoint REST. Si es el último,
  // el backend marca la boleta como 'aplicada' (requiere bodega cerró todos
  // + boomero finalizó todos).
  async function finalizarBoom(boom_num: number) {
    if (!modal) return;
    setProcesandoBoom(boom_num);
    try {
      const { lat, lng } = await capturarGpsPuntual();
      const tipo: 'completo' | 'fraccion' = boom_num === 0 ? 'fraccion' : 'completo';
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${modal.boleta.id}/finalizar-boom-aplicacion`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ boom_num, tipo, lat, lng }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        showMsg('err', j?.error || 'No se pudo finalizar el boom.');
        return;
      }
      await cargarBoomsAplicados(modal.boleta.id);

      // Si la boleta pasó a aplicada (última carga), avisamos y disparamos
      // la aplicación de inventario.
      if (j.nuevo_estado === 'aplicada') {
        showMsg('ok', '¡Aplicación completa! La boleta quedó aplicada.');
        try {
          await fetch(buildApiUrl(`/aplicaciones/boletas/${modal.boleta.id}/aplicar-inventario`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-empresa-id': String(empresaId),
              ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
            },
          });
        } catch { /* best-effort */ }
        // Refrescar la lista principal del boomero
        await cargar();
      } else {
        showMsg('ok', 'Boom finalizado');
      }
    } finally {
      setProcesandoBoom(null);
    }
  }

  function setCampo(fn: (prev: AppBoletaCampo) => AppBoletaCampo) {
    setModal(prev => prev ? { ...prev, campo: fn(prev.campo) } : prev);
  }

  // ── Capturar GPS al iniciar/finalizar y guardar parcial ────────────────────
  // (capturandoGps se declara arriba para que useGpsTracker pueda pausarse)

  // ── Auto-save: cada vez que cambia `modal.campo`, agendar guardado en 2s.
  // Si el boomero cierra la app antes, los datos quedan persistidos en BD.
  // No requiere acción manual del boomero. NO se ejecuta sin un id previo (solo
  // se activa después de "Iniciar aplicación" que crea la fila inicial).
  useEffect(() => {
    if (!modal?.campo?.id) return; // requiere fila existente (creada en marcarInicio)
    const campoSerial = JSON.stringify(modal.campo);
    // Si nada cambió respecto a la última sincronización, no hacer nada
    if (campoSerial === ultimaCampoSerialRef.current) return;
    setAutoSave('pendiente');
    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(async () => {
      try {
        setAutoSave('guardando');
        const snapshot = modal.campo;
        const payload = {
          ...snapshot,
          registrado_por: snapshot.registrado_por || usuario,
          updated_at: new Date().toISOString(),
        };
        const { error } = await supabase
          .from('app_boleta_campo')
          .update(payload)
          .eq('id', snapshot.id!);
        if (error) throw error;
        ultimaCampoSerialRef.current = campoSerial;
        setAutoSave('guardado');
      } catch (err: any) {

        setAutoSave('error');
      }
    }, 2000);
    return () => {
      if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal?.campo]);

  // Resetear el auto-save al cerrar el modal
  useEffect(() => {
    if (!modal) {
      if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
      setAutoSave('idle');
      ultimaCampoSerialRef.current = '';
    } else if (modal.campo?.id) {
      // al abrir, establecer el baseline
      ultimaCampoSerialRef.current = JSON.stringify(modal.campo);
    }
  }, [modal]);

  async function marcarInicio() {
    if (!modal) return;
    setCapturandoGps('inicio');
    try {
      const pos = await obtenerUbicacion();
      // Snapshot meteorológico (Open-Meteo) — best-effort, no bloquea si falla
      const clima = await consultarClimaOpenMeteo(pos.lat, pos.lng).catch((e) => {

        return null;
      });
      // Guardar inmediatamente (upsert) para no perder el dato
      const base: AppBoletaCampo = {
        ...modal.campo,
        inicio_at: pos.at,
        inicio_lat: pos.lat,
        inicio_lng: pos.lng,
        inicio_precision: pos.accuracy,
        // Clima al inicio (si se obtuvo)
        ...(clima ? {
          clima_temp_c: clima.temp_c,
          clima_humedad_pct: clima.humedad_pct,
          clima_viento_kmh: clima.viento_kmh,
          clima_viento_dir_deg: clima.viento_dir_deg,
          clima_precipitacion_mm: clima.precipitacion_mm,
          clima_codigo: clima.codigo,
          clima_fuente: 'open-meteo',
        } : {}),
        registrado_por: usuario,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = base.id
        ? await supabase.from('app_boleta_campo').update(base).eq('id', base.id).select().single()
        : await supabase.from('app_boleta_campo').insert(base).select().single();
      if (error) throw error;
      setCampo(() => data as AppBoletaCampo);
      const detClima = clima ? ` · ${clima.temp_c.toFixed(0)}°C, ${clima.humedad_pct.toFixed(0)}% HR, viento ${clima.viento_kmh.toFixed(0)} km/h` : '';
      showMsg('ok', `Inicio registrado: ${fmtCoord(pos.lat, pos.lng)} (±${pos.accuracy.toFixed(0)}m)${detClima}`);
    } catch (e: any) {
      showMsg('err', e?.message || 'No se pudo capturar el GPS de inicio.');
    } finally {
      setCapturandoGps(null);
    }
  }

  async function marcarFin() {
    if (!modal) return;
    setCapturandoGps('fin');
    try {
      const pos = await obtenerUbicacion();
      const base: AppBoletaCampo = {
        ...modal.campo,
        fin_at: pos.at,
        fin_lat: pos.lat,
        fin_lng: pos.lng,
        fin_precision: pos.accuracy,
        registrado_por: usuario,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = base.id
        ? await supabase.from('app_boleta_campo').update(base).eq('id', base.id).select().single()
        : await supabase.from('app_boleta_campo').insert(base).select().single();
      if (error) throw error;
      setCampo(() => data as AppBoletaCampo);
      showMsg('ok', `Fin registrado: ${fmtCoord(pos.lat, pos.lng)} (±${pos.accuracy.toFixed(0)}m)`);
    } catch (e: any) {
      showMsg('err', e?.message || 'No se pudo capturar el GPS de fin.');
    } finally {
      setCapturandoGps(null);
    }
  }

  // ── Guardar reporte de campo ────────────────────────────────────────────────

  async function guardar() {
    if (!modal) return;
    const { campo } = modal;

    if (!campo.fin_at) {
      showMsg('err', 'Marcá "Finalizar aplicación" cuando termines de aplicar para poder guardar.'); return;
    }
    if (!campo.boom_id) {
      showMsg('err', 'Seleccione el boom utilizado.'); return;
    }
    // validar al menos 1 método y 1 clima
    if (!campo.metodo_aspersion && !campo.metodo_inmersion && !campo.metodo_nebulizacion) {
      showMsg('err', 'Seleccione el método de aplicación.'); return;
    }

    setGuardando(true);
    const payload = { ...campo, registrado_por: usuario, updated_at: new Date().toISOString() };

    const { error } = campo.id
      ? await supabase.from('app_boleta_campo').update(payload).eq('id', campo.id)
      : await supabase.from('app_boleta_campo').insert(payload);

    setGuardando(false);
    if (error) { showMsg('err', error.message); return; }
    showMsg('ok', 'Reporte de campo guardado.');
    setModal(null);
    cargar();
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100%', background: 'var(--surface-base)' }}>
      <ConexionBanner />
      <div className="boomero-page" style={{ padding: '16px' }}>

      {msg && (
        <div className="boomero-modal-shell" style={{
          padding: '14px 16px', borderRadius: '12px', marginBottom: '16px',
          fontWeight: 700, fontSize: '15px',
          background: msg.tipo === 'ok' ? '#0f2c20' : '#34181c',
          border: `1px solid ${msg.tipo === 'ok' ? '#1d6e4f' : '#7d2f3a'}`,
          color: msg.tipo === 'ok' ? '#9df4c7' : '#ffb3bb',
        }}>{msg.texto}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc' }}>Terminal Boomero</div>
          <div style={{ fontSize: '13px', color: 'var(--ink-muted)' }}>Boletas en proceso de aplicación</div>
        </div>
        <button onClick={cargar} style={{
          padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
          border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)',
          color: 'var(--ink-muted)', cursor: 'pointer',
        }}>↺</button>
      </div>

      {loading && <p style={{ color: 'var(--ink-muted)', textAlign: 'center', padding: '40px' }}>Cargando...</p>}

      {(() => {
        // Una boleta desaparece de la lista del boomero cuando ya se marcó "Finalizar aplicación".
        // Si quiere reabrir/editar, lo puede hacer desde Bodega o Cédulas (historial).
        const boletasVisibles = boletas.filter(b => {
          const rep = reportePorBoleta.get(Number(b.id));
          return !rep?.fin_at;
        });
        if (!loading && boletasVisibles.length === 0) {
          return (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--ink-muted)', fontSize: '16px' }}>
              No hay aplicaciones pendientes de reporte
            </div>
          );
        }
        return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {boletasVisibles.map(b => {
          const rep = reportePorBoleta.get(Number(b.id));
          const tieneReporte = !!rep;
          const borderColor = tieneReporte ? 'rgba(34,197,94,0.5)' : '#f59e0b';
          const bgCard = tieneReporte ? 'rgba(34,197,94,0.06)' : 'rgba(245,158,11,0.07)';
          // Progreso de booms aplicados
          const nBoomsCompletos = Number(b.n_booms_completos ?? 0);
          const tieneFraccion = Number(b.litros_fraccion ?? 0) > 0;
          const totalBooms = nBoomsCompletos + (tieneFraccion ? 1 : 0);
          const boomsAplicados = boomsPorBoleta.get(Number(b.id)) ?? new Set<number>();
          const boomsCerradosBodega = boomsDespPorBoleta.get(Number(b.id)) ?? new Set<number>();
          return (
          <button key={b.id} className="boomero-list-card" onClick={() => abrirFormulario(b)} style={{
            width: '100%', textAlign: 'left', padding: '18px 20px', borderRadius: '14px',
            border: `1.5px solid ${borderColor}`, background: bgCard,
            cursor: 'pointer',
          }}>
            <div className="boomero-list-title" style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc', marginBottom: '4px' }}>
              Lote {b.lote_num ?? '—'} &nbsp;
              <span style={{ fontSize: '14px', fontWeight: 600, color: tieneReporte ? '#4ade80' : '#fbbf24' }}>Sem. {b.semana}</span>
            </div>
            <div style={{ fontSize: '14px', color: 'var(--ink-muted)' }}>
              {b.etapa_nombre ?? '—'} · {b.finca_nombre ?? '—'}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--ink-faint)', marginTop: '2px' }}>
              {b.bloques} · {fmtN(b.area_ha, 2)} ha · <span style={{ letterSpacing: '0.02em' }}>#{b.numero}</span>
            </div>

            {/* Chips de progreso por boom — 3 estados:
                  aplicado ✓ (verde) / listo para aplicar (azul, bodega cerró) / esperando bodega (gris) */}
            {totalBooms > 0 && (() => {
              // Estilo de chip según el estado del boom (num: 1..N, 0=fracción)
              const chipStyle = (num: number, esFrac: boolean) => {
                const aplicado = boomsAplicados.has(num);
                const listo = !aplicado && boomsCerradosBodega.has(num);
                if (aplicado) return { bg: 'rgba(34,197,94,0.18)', border: 'rgba(34,197,94,0.45)', color: '#4ade80', icon: '✓' };
                if (listo)   return { bg: 'rgba(59,130,246,0.16)', border: 'rgba(59,130,246,0.5)',  color: '#93c5fd', icon: '🚜' };
                // esperando bodega — la fracción mantiene su tinte ámbar tenue
                return esFrac
                  ? { bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.25)', color: 'var(--ink-faint)', icon: '' }
                  : { bg: 'rgba(255,255,255,0.05)', border: 'rgba(148,163,184,0.25)', color: 'var(--ink-faint)', icon: '' };
              };
              // Solo mostramos chips de booms RELEVANTES para el boomero:
              // los que ya aplicó (✓) o los que bodega ya cerró (🚜 listos).
              // Los que bodega aún no prepara se ocultan completamente.
              const visible = (num: number) =>
                boomsAplicados.has(num) || boomsCerradosBodega.has(num);
              const completosVisibles = Array.from({ length: nBoomsCompletos }, (_, i) => i + 1).filter(visible);
              const fraccionVisible = tieneFraccion && visible(0);
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                  {completosVisibles.map(n => {
                    const s = chipStyle(n, false);
                    return (
                      <span key={`b${n}`} style={{
                        padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700,
                        background: s.bg, border: `1px solid ${s.border}`, color: s.color,
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                      }}>
                        {s.icon} Boom {n}
                      </span>
                    );
                  })}
                  {fraccionVisible && (() => {
                    const s = chipStyle(0, true);
                    return (
                      <span style={{
                        padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700,
                        background: s.bg, border: `1px solid ${s.border}`, color: s.color,
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                      }}>
                        {s.icon} Fracción
                      </span>
                    );
                  })()}
                  {/* Contador: total real (aplicados / total esperado de la boleta) */}
                  <span style={{
                    padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700,
                    background: 'rgba(255,255,255,0.04)', color: 'var(--ink-muted)',
                    marginLeft: 'auto',
                  }}>
                    {boomsAplicados.size} / {totalBooms}
                  </span>
                </div>
              );
            })()}

            {/* Info del reporte si existe */}
            {rep && (() => {
              const fechaIso = rep.registrado_at || rep.updated_at;
              const fechaTxt = fechaIso
                ? new Date(fechaIso).toLocaleString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : null;
              const nombre = rep.boomero_nombre || rep.registrado_por;
              return (
                <div style={{
                  marginTop: '10px', padding: '8px 10px', borderRadius: '8px',
                  background: 'rgba(59,130,246,0.10)',
                  border: '1px solid rgba(59,130,246,0.22)',
                  fontSize: '12px', color: 'var(--ink-muted)', lineHeight: 1.4,
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
                    {nombre && (
                      <span><span style={{ color: '#93c5fd', fontWeight: 700 }}>Boomero:</span> {nombre}</span>
                    )}
                    {fechaTxt && (
                      <span><span style={{ color: '#93c5fd', fontWeight: 700 }}>Aplicado:</span> {fechaTxt}</span>
                    )}
                  </div>
                  {rep.observaciones && rep.observaciones.trim() && (
                    <div style={{ marginTop: '4px', fontStyle: 'italic', color: 'var(--ink-faint)' }}>
                      “{rep.observaciones}”
                    </div>
                  )}
                </div>
              );
            })()}

          </button>
          );
        })}
      </div>
        );
      })()}

      {/* ── Modal formulario de campo ── */}
      {modal && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)',
          overflowY: 'auto', padding: '16px',
        }}>
          <div className="boomero-modal-card" style={{
            maxWidth: '560px', margin: '0 auto', borderRadius: '18px', padding: '24px',
            background: '#0d1117', border: '1px solid var(--line)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
          }}>
            {/* Header */}
            <div className="boomero-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc' }}>Reporte de Campo</div>
                <div style={{ fontSize: '13px', color: 'var(--ink-muted)' }}>
                  Lote {modal.boleta.lote_num} · {modal.boleta.etapa_nombre} · Sem. {modal.boleta.semana}
                </div>
              </div>
              <button onClick={() => setModal(null)} style={{
                padding: '6px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
              }}>Cerrar</button>
            </div>

            {/* Indicador de auto-guardado */}
            {modal.campo.id && (
              <div style={{
                fontSize: '11px', fontWeight: 600, padding: '6px 10px', borderRadius: '6px',
                marginBottom: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: autoSave === 'guardado'  ? 'rgba(34,197,94,0.08)'
                        : autoSave === 'guardando' ? 'rgba(59,130,246,0.10)'
                        : autoSave === 'pendiente' ? 'rgba(245,158,11,0.10)'
                        : autoSave === 'error'     ? 'rgba(239,68,68,0.10)'
                        : 'rgba(255,255,255,0.04)',
                color: autoSave === 'guardado'  ? '#4ade80'
                     : autoSave === 'guardando' ? '#93c5fd'
                     : autoSave === 'pendiente' ? '#fbbf24'
                     : autoSave === 'error'     ? '#fca5a5'
                     : 'var(--ink-faint)',
                border: '1px solid',
                borderColor: autoSave === 'guardado'  ? 'rgba(34,197,94,0.25)'
                          : autoSave === 'guardando' ? 'rgba(59,130,246,0.30)'
                          : autoSave === 'pendiente' ? 'rgba(245,158,11,0.30)'
                          : autoSave === 'error'     ? 'rgba(239,68,68,0.30)'
                          : 'transparent',
              }}>
                {autoSave === 'guardado'  ? '✓ Guardado automáticamente'
                : autoSave === 'guardando' ? '⏳ Guardando...'
                : autoSave === 'pendiente' ? '… cambios sin guardar'
                : autoSave === 'error'     ? '⚠ Error al guardar — revise conexión'
                : '✓ Sin cambios'}
              </div>
            )}

            {/* El bloque "Inicio y fin de aplicación" global se eliminó: ahora cada
                boom registra su propio inicio/fin en "Aplicación por booms". El
                reporte de campo consolidado (app_boleta_campo) lo arma el backend
                automáticamente al finalizar el último boom. */}
            {false && (
            <Seccion titulo="Inicio y fin de aplicación">
              <div className="boomero-field-grid">
                {/* INICIO */}
                <div style={{
                  padding: '12px', borderRadius: '12px',
                  border: `1.5px solid ${modal.campo.inicio_at ? 'rgba(34,197,94,0.45)' : 'rgba(255,255,255,0.12)'}`,
                  background: modal.campo.inicio_at ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.03)',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: modal.campo.inicio_at ? '#4ade80' : 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                    Inicio
                  </div>
                  {modal.campo.inicio_at ? (
                    <div style={{ fontSize: '12px', color: 'var(--ink)', lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 600 }}>{fmtDateTime(modal.campo.inicio_at)}</div>
                      <div style={{ fontSize: '11px', color: 'var(--ink-muted)', fontFamily: 'monospace', marginTop: '2px' }}>
                        {fmtCoord(modal.campo.inicio_lat, modal.campo.inicio_lng)}
                      </div>
                      {modal.campo.inicio_precision != null && (
                        <div style={{ fontSize: '10px', color: 'var(--ink-faint)' }}>
                          ± {Number(modal.campo.inicio_precision).toFixed(0)} m
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: 'var(--ink-faint)', fontStyle: 'italic' }}>
                      Sin registrar
                    </div>
                  )}
                  {modal.campo.inicio_at ? (
                    <div style={{
                      width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px',
                      fontSize: '13px', fontWeight: 700,
                      background: 'rgba(34,197,94,0.20)', color: '#86efac',
                      textAlign: 'center', cursor: 'default',
                      border: '1px solid rgba(34,197,94,0.35)',
                    }}>
                      ✓ Iniciado · {new Date(modal.campo.inicio_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  ) : (
                    <button
                      onClick={marcarInicio}
                      disabled={capturandoGps !== null || guardando}
                      style={{
                        width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px',
                        fontSize: '13px', fontWeight: 700,
                        border: 'none', cursor: capturandoGps !== null ? 'wait' : 'pointer',
                        background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                        color: '#fff',
                        opacity: capturandoGps !== null ? 0.6 : 1,
                      }}>
                      {capturandoGps === 'inicio' ? 'Capturando GPS...' : '▶ Iniciar aplicación'}
                    </button>
                  )}
                </div>

                {/* FIN */}
                <div style={{
                  padding: '12px', borderRadius: '12px',
                  border: `1.5px solid ${modal.campo.fin_at ? 'rgba(239,68,68,0.45)' : 'rgba(255,255,255,0.12)'}`,
                  background: modal.campo.fin_at ? 'rgba(239,68,68,0.06)' : 'rgba(255,255,255,0.03)',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: modal.campo.fin_at ? '#fca5a5' : 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                    Fin
                  </div>
                  {modal.campo.fin_at ? (
                    <div style={{ fontSize: '12px', color: 'var(--ink)', lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 600 }}>{fmtDateTime(modal.campo.fin_at)}</div>
                      <div style={{ fontSize: '11px', color: 'var(--ink-muted)', fontFamily: 'monospace', marginTop: '2px' }}>
                        {fmtCoord(modal.campo.fin_lat, modal.campo.fin_lng)}
                      </div>
                      {modal.campo.fin_precision != null && (
                        <div style={{ fontSize: '10px', color: 'var(--ink-faint)' }}>
                          ± {Number(modal.campo.fin_precision).toFixed(0)} m
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: 'var(--ink-faint)', fontStyle: 'italic' }}>
                      Sin registrar
                    </div>
                  )}
                  {modal.campo.fin_at ? (
                    <div style={{
                      width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px',
                      fontSize: '13px', fontWeight: 700,
                      background: 'rgba(239,68,68,0.20)', color: '#fca5a5',
                      textAlign: 'center', cursor: 'default',
                      border: '1px solid rgba(239,68,68,0.35)',
                    }}>
                      ✓ Finalizado · {new Date(modal.campo.fin_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  ) : (
                    <button
                      onClick={marcarFin}
                      disabled={capturandoGps !== null || guardando || !modal.campo.inicio_at}
                      title={!modal.campo.inicio_at ? 'Marque primero el inicio' : ''}
                      style={{
                        width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px',
                        fontSize: '13px', fontWeight: 700,
                        border: 'none', cursor: capturandoGps !== null || !modal.campo.inicio_at ? 'not-allowed' : 'pointer',
                        background: !modal.campo.inicio_at ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg,#dc2626,#ef4444)',
                        color: !modal.campo.inicio_at ? 'rgba(255,255,255,0.3)' : '#fff',
                        opacity: capturandoGps !== null ? 0.6 : 1,
                      }}>
                      {capturandoGps === 'fin' ? 'Capturando GPS...' : '■ Finalizar aplicación'}
                    </button>
                  )}
                </div>
              </div>
              {modal.campo.inicio_at && modal.campo.fin_at && (() => {
                const ms = new Date(modal.campo.fin_at).getTime() - new Date(modal.campo.inicio_at).getTime();
                if (!Number.isFinite(ms) || ms <= 0) return null;
                const minutos = Math.round(ms / 60000);
                const horas = Math.floor(minutos / 60);
                const mins = minutos % 60;
                return (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#93c5fd', textAlign: 'center' }}>
                    Duración: {horas > 0 ? `${horas} h ${mins} min` : `${mins} min`}
                  </div>
                );
              })()}
              {trackerActivo && (
                <div style={{
                  marginTop: '12px', padding: '10px 14px', borderRadius: '10px',
                  border: '1px solid rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  fontSize: '13px', color: '#4ade80', fontWeight: 600,
                }}>
                  <span style={{
                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                    background: '#22c55e', boxShadow: '0 0 0 0 rgba(34,197,94,0.6)',
                    animation: 'pulse-gps 1.6s ease-in-out infinite',
                  }} />
                  Rastreando GPS · {trackPuntos} capturado{trackPuntos === 1 ? '' : 's'} · {trackGuardados} guardado{trackGuardados === 1 ? '' : 's'}
                </div>
              )}
            </Seccion>
            )}

            {/* Aplicación por booms — varios equipos pueden aplicar en paralelo */}
            <Seccion titulo="Aplicación por booms">
              {(() => {
                const nCompletos = Number(modal.boleta.n_booms_completos ?? 0);
                const litrosBoom = Number(modal.boleta.boom_capacidad_l ?? 0);
                const litrosFraccion = Number(modal.boleta.litros_fraccion ?? 0);
                const items: { num: number; tipo: 'completo' | 'fraccion'; label: string; litros: number }[] = [];
                for (let i = 1; i <= nCompletos; i++) {
                  items.push({ num: i, tipo: 'completo', label: `Boom ${i}`, litros: litrosBoom });
                }
                if (litrosFraccion > 0) {
                  items.push({ num: 0, tipo: 'fraccion', label: 'Fracción', litros: litrosFraccion });
                }
                const aplicadosMap = new Map(boomsAplicados.map(b => [b.boom_num, b]));
                const totalFinalizados = items.filter(it => {
                  const a = aplicadosMap.get(it.num);
                  return a && a.fin_at;
                }).length;
                const totalItems = items.length;

                if (totalItems === 0) {
                  return (
                    <div style={{ fontSize: '13px', color: 'var(--ink-faint)', fontStyle: 'italic', padding: '12px' }}>
                      Esta cédula no tiene booms calculados.
                    </div>
                  );
                }

                return (
                  <>
                    {/* Barra de progreso */}
                    <div style={{
                      marginBottom: '14px', padding: '10px 14px', borderRadius: '10px',
                      background: 'rgba(255,255,255,0.03)', border: '1px solid var(--line-dim)',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                    }}>
                      <div style={{ fontSize: '13px', color: 'var(--ink)', fontWeight: 600 }}>
                        {totalFinalizados} de {totalItems} booms aplicados
                      </div>
                      <div style={{ flex: 1, height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden', maxWidth: '200px' }}>
                        <div style={{
                          height: '100%',
                          width: `${totalItems > 0 ? (totalFinalizados / totalItems) * 100 : 0}%`,
                          background: 'linear-gradient(90deg,#16a34a,#22c55e)',
                          transition: 'width 0.3s',
                        }} />
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--ink-muted)' }}>
                        Total: {(nCompletos * litrosBoom + litrosFraccion).toLocaleString('es-CR')} L
                      </div>
                    </div>

                    {/* Lista de booms con botones grandes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {items.map((it) => {
                        const aplicado = aplicadosMap.get(it.num);
                        const esFraccion = it.tipo === 'fraccion';
                        const finalizado = !!aplicado?.fin_at;
                        const esEnCurso = aplicado && aplicado.inicio_at && !aplicado.fin_at;
                        // Bodega: ¿este boom ya fue cerrado (despachado) por el bodeguero?
                        const desp = boomsDespachados.find(d => d.boom_num === it.num);
                        const bodegaCerro = desp != null && desp.estado === 'despachado';
                        const bodegaDescartado = desp != null && desp.estado === 'descartado';
                        // SIN orden secuencial: varios equipos pueden aplicar en
                        // paralelo. Cualquier boom cerrado por bodega y no aplicado
                        // se puede iniciar de forma independiente (no espera a otro).
                        const puedeIniciar = bodegaCerro && !aplicado;
                        return (
                          <div key={it.num}
                            style={{
                              padding: '14px 16px', borderRadius: '12px',
                              border: `2px solid ${
                                finalizado ? 'rgba(34,197,94,0.45)' :
                                esEnCurso ? 'rgba(59,130,246,0.55)' :
                                esFraccion ? 'rgba(251,191,36,0.3)' :
                                'rgba(255,255,255,0.12)'
                              }`,
                              background: finalizado ? 'rgba(34,197,94,0.06)'
                                       : esEnCurso ? 'rgba(59,130,246,0.08)'
                                       : esFraccion ? 'rgba(251,191,36,0.04)'
                                       : 'rgba(255,255,255,0.03)',
                            }}>
                            {/* Header del boom */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: (esEnCurso || puedeIniciar) ? '12px' : '0' }}>
                              <div style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: `2.5px solid ${finalizado ? '#22c55e' : esEnCurso ? '#3b82f6' : 'rgba(148,163,184,0.3)'}`,
                                background: finalizado ? 'linear-gradient(135deg,#16a34a,#22c55e)'
                                         : esEnCurso ? 'linear-gradient(135deg,#2563eb,#3b82f6)'
                                         : 'rgba(255,255,255,0.04)',
                                color: '#fff', fontSize: '20px', fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              }}>{finalizado ? '✓' : esEnCurso ? '●' : ''}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: '18px', fontWeight: 800,
                                  color: finalizado ? '#4ade80'
                                       : esEnCurso ? '#93c5fd'
                                       : esFraccion ? '#fbbf24'
                                       : 'var(--ink)',
                                }}>
                                  {it.label} · {it.litros.toLocaleString('es-CR')} L
                                </div>
                                {aplicado?.inicio_at && (
                                  <div style={{ fontSize: '12px', color: 'var(--ink-muted)', marginTop: '3px' }}>
                                    Inicio: {new Date(aplicado.inicio_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                                    {aplicado.aplicado_por && <span> · {aplicado.aplicado_por}</span>}
                                    {aplicado.fin_at && (
                                      <span> · Fin: {new Date(aplicado.fin_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Botones grandes Iniciar / Finalizar */}
                            {puedeIniciar && (
                              <button
                                onClick={() => iniciarBoom(it.num, it.tipo, it.litros)}
                                disabled={procesandoBoom !== null}
                                style={{
                                  width: '100%', padding: '16px', borderRadius: '12px',
                                  fontSize: '18px', fontWeight: 800,
                                  border: 'none',
                                  cursor: procesandoBoom !== null ? 'wait' : 'pointer',
                                  background: esFraccion ? 'linear-gradient(135deg,#d97706,#f59e0b)'
                                                         : 'linear-gradient(135deg,#16a34a,#22c55e)',
                                  color: '#fff',
                                  opacity: procesandoBoom !== null && procesandoBoom !== it.num ? 0.5 : 1,
                                  letterSpacing: '0.02em',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                }}>
                                {procesandoBoom === it.num ? '⏳ Capturando GPS…' : `▶ Iniciar ${it.label}`}
                              </button>
                            )}

                            {esEnCurso && (
                              <button
                                onClick={() => finalizarBoom(it.num)}
                                disabled={procesandoBoom !== null}
                                style={{
                                  width: '100%', padding: '16px', borderRadius: '12px',
                                  fontSize: '18px', fontWeight: 800,
                                  border: 'none',
                                  cursor: procesandoBoom !== null ? 'wait' : 'pointer',
                                  background: 'linear-gradient(135deg,#dc2626,#ef4444)',
                                  color: '#fff',
                                  opacity: procesandoBoom !== null && procesandoBoom !== it.num ? 0.5 : 1,
                                  letterSpacing: '0.02em',
                                  boxShadow: '0 4px 12px rgba(220,38,38,0.4)',
                                }}>
                                {procesandoBoom === it.num ? '⏳ Capturando GPS…' : `■ Finalizar ${it.label}`}
                              </button>
                            )}

                            {/* Mensajes de bloqueo */}
                            {!aplicado && bodegaDescartado && (
                              <div style={{
                                fontSize: '12.5px', color: '#fca5a5', textAlign: 'center', padding: '10px',
                                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                                borderRadius: '8px', fontWeight: 600,
                              }}>
                                ✕ Fracción descartada por bodega
                              </div>
                            )}
                            {!aplicado && !bodegaCerro && !bodegaDescartado && (
                              <div style={{
                                fontSize: '12.5px', color: '#fbbf24', textAlign: 'center', padding: '10px',
                                background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)',
                                borderRadius: '8px', fontWeight: 600,
                              }}>
                                ⌛ Esperando a que bodega prepare este boom
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </Seccion>

            {/* Boomero y equipo */}
            <Seccion titulo="Operario y equipo">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: '4px' }}>OPERARIO</div>
                  <div style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '15px',
                    border: '1.5px solid var(--line)', background: 'rgba(255,255,255,0.03)',
                    color: 'var(--ink)', fontWeight: 600,
                  }}>
                    {modal.campo.boomero_nombre || usuario || '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: '8px' }}>BOOM *</div>
                  {booms.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#f59e0b', padding: '10px 12px', borderRadius: '8px', border: '1px dashed rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)' }}>
                      No hay booms configurados. Cargá los booms en Plan de Manejo.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                      {booms.map(b => {
                        const seleccionado = modal.campo.boom_id === b.id;
                        return (
                          <button key={b.id} onClick={() => setCampo(p => ({
                            ...p,
                            boom_id: b.id,
                            boom_descripcion: `${b.nombre}${b.capacidad_l ? ` · ${b.capacidad_l} L` : ''}`,
                          }))} style={{
                            padding: '12px 10px', borderRadius: '10px',
                            cursor: 'pointer', textAlign: 'left',
                            border: `1.5px solid ${seleccionado ? '#3b82f6' : 'rgba(255,255,255,0.12)'}`,
                            background: seleccionado ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                            color: seleccionado ? '#93c5fd' : 'var(--ink)',
                            transition: 'all 0.15s',
                          }}>
                            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '2px' }}>{b.nombre}</div>
                            <div style={{ fontSize: '11px', color: seleccionado ? '#93c5fd' : 'var(--ink-faint)' }}>
                              {b.capacidad_l ? `${b.capacidad_l} L` : ''}{b.descripcion ? ` · ${b.descripcion}` : ''}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Seccion>

            {/* Calibración */}
            <Seccion titulo="Información de calibración">
              <div className="boomero-calibration-row">
                <NumInput label="RPM" value={modal.campo.rpm} placeholder="0" onChange={v => setCampo(p => ({ ...p, rpm: v }))} />
                <NumInput label="PRESIÓN (bar)" value={modal.campo.presion_bar} placeholder="0.00" onChange={v => setCampo(p => ({ ...p, presion_bar: v }))} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: '8px' }}>MARCHA</div>
                <div className="boomero-chip-row">
                  {MARCHAS.map(m => (
                    <button key={m} onClick={() => setCampo(p => ({ ...p, marcha: m }))} style={{
                      padding: '10px 18px', borderRadius: '10px', fontSize: '15px', fontWeight: 700,
                      cursor: 'pointer',
                      border: `1.5px solid ${modal.campo.marcha === m ? '#3b82f6' : 'rgba(255,255,255,0.12)'}`,
                      background: modal.campo.marcha === m ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                      color: modal.campo.marcha === m ? '#93c5fd' : 'var(--ink-muted)',
                    }}>{m}</button>
                  ))}
                </div>
              </div>
            </Seccion>

            {/* Método de aplicación */}
            <Seccion titulo="Método de aplicación *">
              <div className="boomero-chip-row">
                <CheckChip label="Aspersión"   checked={modal.campo.metodo_aspersion}   onChange={v => setCampo(p => ({ ...p, metodo_aspersion: v }))} />
                <CheckChip label="Inmersión"   checked={modal.campo.metodo_inmersion}   onChange={v => setCampo(p => ({ ...p, metodo_inmersion: v }))} />
                <CheckChip label="Nebulización" checked={modal.campo.metodo_nebulizacion} onChange={v => setCampo(p => ({ ...p, metodo_nebulizacion: v }))} />
              </div>
            </Seccion>

            {/* Área de aplicación */}
            <Seccion titulo="Aplicado en">
              <div className="boomero-chip-row">
                <CheckChip label="Cultivo"          checked={modal.campo.cultivo}       onChange={v => setCampo(p => ({ ...p, cultivo: v }))} />
                <CheckChip label="Semillero"        checked={modal.campo.semillero}     onChange={v => setCampo(p => ({ ...p, semillero: v }))} />
                <CheckChip label="Área de barbecho" checked={modal.campo.area_barbecho} onChange={v => setCampo(p => ({ ...p, area_barbecho: v }))} />
              </div>
            </Seccion>

            {/* Caldo sobrante */}
            <Seccion titulo="Caldo sobrante">
              <div className="boomero-chip-row" style={{ alignItems: 'center' }}>
                <CheckChip label="Sí"  checked={modal.campo.caldo_sobrante === true}  onChange={() => setCampo(p => ({ ...p, caldo_sobrante: true }))} />
                <CheckChip label="No"  checked={modal.campo.caldo_sobrante === false} onChange={() => setCampo(p => ({ ...p, caldo_sobrante: false, caldo_sobrante_litros: undefined }))} />
                {modal.campo.caldo_sobrante && (
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <NumInput label="LITROS SOBRANTES" value={modal.campo.caldo_sobrante_litros} placeholder="0.00" onChange={v => setCampo(p => ({ ...p, caldo_sobrante_litros: v }))} />
                  </div>
                )}
              </div>
            </Seccion>

            {/* Condiciones climáticas */}
            <Seccion titulo="Condiciones climáticas">
              <div className="boomero-chip-row">
                <CheckChip label="Nublado"  checked={modal.campo.clima_nublado}  onChange={v => setCampo(p => ({ ...p, clima_nublado: v }))} />
                <CheckChip label="Lluvioso" checked={modal.campo.clima_lluvioso} onChange={v => setCampo(p => ({ ...p, clima_lluvioso: v }))} />
                <CheckChip label="Soleado"  checked={modal.campo.clima_soleado}  onChange={v => setCampo(p => ({ ...p, clima_soleado: v }))} />
                <CheckChip label="Ventoso"  checked={modal.campo.clima_ventoso}  onChange={v => setCampo(p => ({ ...p, clima_ventoso: v }))} />
                <CheckChip label="No Apto"  checked={modal.campo.clima_no_apto}  onChange={v => setCampo(p => ({ ...p, clima_no_apto: v }))} />
              </div>
            </Seccion>

            {/* Observaciones */}
            <Seccion titulo="Observaciones">
              <textarea
                value={modal.campo.observaciones ?? ''}
                onChange={e => setCampo(p => ({ ...p, observaciones: e.target.value }))}
                placeholder="Notas adicionales..."
                rows={3}
                style={{
                  width: '100%', padding: '12px', borderRadius: '10px', fontSize: '15px',
                  border: '1px solid var(--line)', background: 'var(--surface-base)',
                  color: '#f8fafc', resize: 'none', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </Seccion>

            {/* Botón guardar — habilitado solo cuando se marca Finalizar aplicación */}
            {(() => {
              const sinFin = !modal.campo.fin_at;
              return (
                <button
                  className="boomero-save-button"
                  onClick={guardar}
                  disabled={guardando || sinFin}
                  style={{
                    width: '100%', padding: '18px', borderRadius: '14px', fontSize: '18px', fontWeight: 800,
                    border: 'none', letterSpacing: '0.02em',
                    background: guardando || sinFin
                      ? 'rgba(255,255,255,0.07)'
                      : 'linear-gradient(135deg,#f59e0b,#fbbf24)',
                    color: guardando || sinFin ? 'rgba(255,255,255,0.4)' : '#0d1117',
                    cursor: guardando || sinFin ? 'not-allowed' : 'pointer',
                  }}>
                  {guardando
                    ? 'Guardando...'
                    : sinFin
                      ? 'Pendiente: marcar Finalizar aplicación'
                      : '✓ Guardar reporte de campo'}
                </button>
              );
            })()}
          </div>
        </div>,
        document.body
      )}
      </div>
    </div>
  );
}
